<template>
  <div class="level-formula-form">
    <div class="level-formula-form-header">
      <drawer-dialog-controls
        :is-pristine="isPristine"
        :is-loading="isLoading"
        @close="$emit('close')"
        @save="saveWithFormat"
      >
        {{ dialogTitle }}
      </drawer-dialog-controls>
    </div>

    <div class="level-formula-form-content">
      <v-card>
        <v-card-title>
          Settings
        </v-card-title>
        <v-divider />

        <v-container>
          <v-row class="mx-0">
            <v-col cols="4" class="pb-0">
              <v-row class="mx-0">
                <v-text-field
                  v-model="localModelValue.name"
                  label="Name"
                  placeholder="Name"
                  hide-details
                />
              </v-row>
            </v-col>
            <v-col cols="4" class="pb-0">
              <v-row class="mx-0">
                <v-text-field
                  v-model="localModelValue.settings.period"
                  label="Period"
                  placeholder="all or current/last month/week"
                  hide-details
                />
              </v-row>
            </v-col>
            <v-col cols="4">
              <v-select
                v-model="localModelValue.entities"
                :items="Object.values(entitiesById)"
                hide-details
                multiple
                :return-object="false"
                label="Projects"
              />
            </v-col>
          </v-row>
          <v-row class="mx-0">
            <v-col cols="4">
              <v-switch
                v-model="localModelValue.settings.hold_level"
                label="Hold level"
                hide-details
              />
            </v-col>
            <v-col cols="4">
              <v-switch
                v-model="localModelValue.settings.slow_drop"
                label="Slow drop"
                hide-details
              />
            </v-col>
            <v-col cols="4">
              <v-text-field
                v-model.number="localModelValue.settings.start_level"
                label="Start level"
                type="number"
                min="0"
                hide-details
              />
            </v-col>
          </v-row>
        </v-container>
      </v-card>
      <v-card class="mt-3">
        <v-card-title>
          Ratio
        </v-card-title>
        <v-divider />

        <v-container>
          <v-row class="mx-0">
            <v-col cols="3" class="pt-0 pb-2">
              <v-text-field
                v-model.number="localModelValue.settings.ratio.xp"
                label="XP"
                placeholder="XP"
                type="number"
                step="0.1"
                max="1"
                min="0"
                hide-details
              />
            </v-col>
            <v-col cols="2" class="pt-0 pb-2">
              <v-text-field
                v-model.number="localModelValue.settings.ratio.bet"
                label="Bet"
                placeholder="Bet"
                type="number"
                step="0.1"
                max="1"
                min="0"
                hide-details
              />
            </v-col>
            <v-col cols="2" class="pt-0 pb-2">
              <v-text-field
                v-model.number="localModelValue.settings.ratio.deposit"
                label="Deposit"
                placeholder="Deposit"
                type="number"
                step="0.1"
                max="1"
                min="0"
                hide-details
              />
            </v-col>
            <v-col cols="2" class="pt-0 pb-2">
              <v-text-field
                v-model.number="localModelValue.settings.ratio.ggr"
                label="GGR"
                placeholder="GGR"
                type="number"
                step="0.1"
                max="1"
                min="0"
                hide-details
              />
            </v-col>
            <v-col cols="3" class="pt-0 pb-2">
              <v-text-field
                v-model.number="localModelValue.settings.ratio.loyalty_points"
                label="Loyalty Points"
                placeholder="Loyalty Points"
                type="number"
                step="0.1"
                max="1"
                min="0"
                hide-details
              />
            </v-col>
          </v-row>
        </v-container>
      </v-card>

      <v-card class="mt-3">
        <v-card-title>
          Map
        </v-card-title>
        <v-divider />

        <v-container>
          <v-row class="mx-0">
            <v-radio-group
              class="pt-0 mt-0"
              v-model="formulaMapType"
              row
            >
              <v-radio
                v-for="(mapTypeLabel, mapTypeValue) in MAP_TYPES"
                :key="mapTypeValue"
                :value="mapTypeValue"
                :label="mapTypeLabel"
              />
            </v-radio-group>

            <v-spacer/>
            <v-btn
              class="mr-2"
              color="success"
              small
              @click="localModelValue.settings.map[formulaMapType].push(0)"
            >
              Add level
            </v-btn>
            <v-btn
              color="warning"
              small
              @click="localModelValue.settings.map[formulaMapType].pop()"
            >
              Remove level
            </v-btn>
          </v-row>
          <v-row class="mx-0">
            <v-col
              v-for="(_, index) in localModelValue.settings.map[formulaMapType]"
              :key="index"
              class="py-1"
              cols="2"
            >
              <v-text-field
                v-model.number="localModelValue.settings.map[formulaMapType][index]"
                type="number"
                hide-details
                min="0"
                :label="`Level ${index + localModelValue.settings.start_level} cap`"
              />
            </v-col>
          </v-row>
        </v-container>
      </v-card>

      <v-card
        v-if="formulaMapType === XP_TYPE"
        class="mt-3"
      >
        <v-card-title>
          XP percentage by game type
        </v-card-title>
        <v-divider />

        <v-container>
          <v-row class="mx-0">
            <v-col cols="2" class="py-1">
              <v-text-field
                v-model.number="localModelValue.settings.xp.default"
                type="number"
                label="Default"
                placeholder="Default"
                suffix="%"
                hide-details
                min="0"
                max="100"
              />
            </v-col>
            <v-col cols="2" class="py-1">
              <v-text-field
                v-model.number="localModelValue.settings.xp.deposit"
                type="number"
                label="Deposit"
                placeholder="Deposit"
                suffix="%"
                hide-details
                min="0"
                max="100"
              />
            </v-col>
            <v-col
              v-for="(gameTypeLabel, gameTypeValue) in GAME_TYPES"
              :key="gameTypeValue"
              cols="2"
              class="py-1"
            >
              <v-text-field
                v-model.number="localModelValue.settings.xp[gameTypeValue]"
                type="number"
                :label="gameTypeLabel"
                :placeholder="gameTypeLabel"
                suffix="%"
                hide-details
                min="0"
                max="100"
              />
            </v-col>
          </v-row>
        </v-container>
      </v-card>

      <v-card class="mt-3">
        <v-card-title>
          Formula JSON
        </v-card-title>
        <v-divider />

        <vue-json-editor
          :value="localModelValue.settings"
          mode="code"
        />
      </v-card>
    </div>
  </div>
</template>

<script>
  import { ref, onMounted, watch, set, del } from 'vue'
  import vueJsonEditor from 'vue-json-editor'
  import { useDialogForm, DrawerDialogControls } from '@/components/core/DrawerDialog'

  const XP_TYPE = 'xp'
  const MAP_TYPES = {
    [XP_TYPE]: 'XP',
    bet: 'Bet',
    deposit: 'Deposit',
    loyalty_points: 'Loyalty points'
  }

  const GAME_TYPES = {
    slots: 'Slots',
    roulette: 'Roulette',
    'live-casino': 'Live casino',
    sports: 'Sports',
    'virtual-sports': 'Virtual sports',
    'table-games': 'Table games',
    blackjack: 'Blackjack',
    baccarat: 'Baccarat',
    poker: 'Poker',
    bingo: 'Bingo',
    scratch: 'Scratch',
    lottery: 'Lottery',
    sicbo: 'Sicbo',
    dice: 'Dice',
    crash: 'Crash'
  }

  export default {
    components: {
      DrawerDialogControls,
      vueJsonEditor,
    },
    props: {
      isLoading: Boolean,
      formula: {
        type: Object,
        default: () => ({})
      },
      entitiesById: {
        type: Object,
        default: () => ({})
      }
    },
    setup (props) {
      const { isPristine, localModelValue } = useDialogForm(props.formula)
      const formulaMapType = ref(null)

      onMounted(() => {
        let hasMapType = false
        for (const mapType in MAP_TYPES) {
          if (localModelValue.value.settings.map[mapType]?.length) {
            formulaMapType.value = mapType
            hasMapType = true
          }
        }

        if (!hasMapType) {
          formulaMapType.value = XP_TYPE
        }

        watch(formulaMapType, (type, oldType) => {
          if (type === XP_TYPE) {
            if (!localModelValue.value.settings.xp) {
              set(localModelValue.value.settings, 'xp', {})
            }
            for (const gameType in GAME_TYPES) {
              set(localModelValue.value.settings.xp, gameType, localModelValue.value.settings.xp[gameType] ?? null)
            }
          }

          if (type && oldType && type !== oldType) {
            set(localModelValue.value.settings.map, type, localModelValue.value.settings.map[oldType])
            del(localModelValue.value.settings.map, oldType)
          }

          if (!(type in localModelValue.value.settings.map)) {
            set(localModelValue.value.settings.map, type, [])
          }
        }, { immediate: true, flush: 'sync' })
      })

      return {
        isPristine,
        localModelValue,
        MAP_TYPES,
        formulaMapType,
        XP_TYPE,
        GAME_TYPES,
      }
    },

    computed: {
      dialogTitle () {
        return this.localModelValue?.formula_id ? `Edit formula #${this.localModelValue.formula_id}` : 'Add new formula'
      }
    },

    methods: {
      saveWithFormat () {
        if (this.formulaMapType !== XP_TYPE) {
          return this.$emit('save', {
            ...this.localModelValue,
            settings: {
              ...this.localModelValue.settings,
              xp: {}
            }
          })
        }

        return this.$emit('save', this.localModelValue)
      }
    }
  }
</script>

<style lang="scss" scoped>
  .level-formula-form {
    &-content {
      padding: 1rem;
    }
  }
</style>
